import { makeAutoObservable, runInAction } from 'mobx'
import { RequestCancelAxios, SingletonRequest } from 'shared/api/lib'
import { logger } from 'shared/lib'
import { ConversationsApi, type IResponseConversationsQuickFilterKey } from 'entities/Conversation'
import { type Conversation } from 'entities/Conversation/model/Conversation'
import { type Message } from 'entities/Message/model/Message'
import {
  type IResponseConversationsFilter,
  type IResponseConversationsFilterKey,
} from 'entities/Conversation/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { Inbox } from 'entities/Inbox/model/Inbox'
import { hasOpenFilter } from 'features/DropdownFilter/lib/hasOpenFilter'
import { hasNeedResponseFilter } from 'features/DropdownFilter/lib/hasNeedResponseFilter'
import { hasOptOutFilter } from 'features/DropdownFilter/lib/hasOptOutFilter'

type ISubscription = (filter: string) => void
type IFiltersStoreProps = {
  disableQuickFilters?: boolean
  showFilterByKeys?: IResponseConversationsFilterKey[]
  onChange?: (filter: IResponseConversationsFilter) => void
  enableCount?: boolean
}

export class FiltersStore {
  private _subscriptions = new Set<ISubscription>()
  private _cancelRequest = new RequestCancelAxios()
  private _loading = true
  private _filtersMap: Map<IResponseConversationsFilterKey, IResponseConversationsFilter> =
    new Map()
  private _filter: IResponseConversationsFilter = {
    key: 'open',
    label: 'Open',
  }
  private _request: null | ReturnType<(typeof ConversationsApi)['getFiltersList']> = null
  private _count = 0

  constructor(private _entities: IEntitiesStore, private _config: IFiltersStoreProps) {
    makeAutoObservable(this)
  }

  get enableCount() {
    return Boolean(this._config.enableCount)
  }

  get loading() {
    return this._loading
  }

  get filter() {
    return this._filter
  }

  get count() {
    return this._count
  }

  setCount = (value: number) => {
    this._count = value
  }

  storeActiveFilter = () => {
    if (!this._entities.usersStore.user) return

    window.localStorage.setItem(this.storageKey, this._filter.key)
  }

  restoreActiveFilter = () => {
    const hasActiveQuickFilter = this.restoreActiveQuickFilter()

    if (!this._entities.usersStore.user || hasActiveQuickFilter) return
    const activeFilterKey = window.localStorage.getItem(
      this.storageKey
    ) as IResponseConversationsFilterKey | null

    if (!activeFilterKey) {
      this.setFilter('open')
      return
    }

    const activeFilter = this.getItem(activeFilterKey)
    if (!activeFilter) return

    this._filter = activeFilter
    this._config.onChange?.(this._filter)
  }

  reset = () => {
    this._loading = false
    this._cancelRequest.reset()
  }

  private _loadFilters = async (): Promise<void> => {
    if (this._request) return this._request.then()

    try {
      this._cancelRequest.init()
      this._loading = true
      this._request = ConversationsApi.getFiltersList({
        cancelToken: this._cancelRequest.token,
      })

      const { data: filters } = await this._request

      this.addItems(filters)
      this.restoreActiveFilter()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  loadFilters = new SingletonRequest(this._loadFilters, { cache: true }).request

  setFilter = (id: IResponseConversationsFilterKey, silent = false) => {
    const filter = this.getItem(id)

    if (filter) {
      this._filter = filter
      this.storeActiveFilter()
      this._config.onChange?.(filter)

      if (!silent) this._subscriptions.forEach((subscription) => subscription(filter.key))
    }
  }

  addItems = (items: IResponseConversationsFilter[]) => {
    items.forEach((item) => {
      this._filtersMap.set(item.key, item)
    })
  }

  getItem = (key: IResponseConversationsFilterKey) => {
    return this._filtersMap.get(key)
  }

  subscribeFilterChange(subscription: ISubscription) {
    this._subscriptions.add(subscription)
  }

  unsubscribeFilterChange(subscription: ISubscription) {
    this._subscriptions.delete(subscription)
  }

  isShowConversation = (conversation: Conversation, message?: Message): boolean => {
    if (this._filter.key === 'open') {
      if (message?.isServerCommand) return true

      return hasOpenFilter(conversation, this._entities)
    }

    if (this._filter.key === 'unread') {
      return conversation.hasUnreadFilter
    }

    if (this._filter.key === 'assigned') {
      return conversation.hasAssignedFilter
    }

    if (this._filter.key === 'unassigned') {
      return conversation.hasUnassignedFilter
    }

    if (this._filter.key === 'pending') {
      return hasNeedResponseFilter(conversation, this._entities)
    }

    if (this._filter.key === 'closed') {
      return conversation.hasClosedFilter
    }

    if (this._filter.key === 'outbound') {
      return conversation.hasOutboundFilter
    }

    if (this._filter.key === 'optout') {
      return hasOptOutFilter(conversation, this._entities)
    }

    if (this._filter.key === 'failed') return false

    if (this._filter.key === 'blocked') {
      return conversation.hasBlockedFilter
    }

    if (this._filter.key === 'mentions') {
      return conversation.hasMentionsFilter
    }

    if (this._filter.key === 'nocalls') {
      return true
    }

    if (this._filter.key === 'draft') {
      return conversation.hasDraftFilter
    }

    const { currentInboxId, currentInbox } = this._entities.inboxesStore

    if (currentInboxId && currentInboxId !== conversation.inbox_id && !currentInbox?.isGroupInbox) {
      return false
    }

    if (currentInbox?.isGroupInbox && !currentInbox.teamsIds.includes(conversation.inbox_id)) {
      return false
    }

    return false
  }

  get quickFilters(): IResponseConversationsQuickFilterKey[] {
    return ['all', 'assigned', 'mentions', 'unassigned', 'draft']
  }

  get quickFilterStorageKey() {
    if (!this._entities.usersStore.user) return ''

    return `${this._entities.usersStore.user.id}_conversation_quick_filter`
  }

  restoreActiveQuickFilter = () => {
    if (!this._entities.usersStore.user) return false

    const activeFilterKey = window.localStorage.getItem(
      this.quickFilterStorageKey
    ) as IResponseConversationsQuickFilterKey | null

    if (!activeFilterKey) return false

    const key = activeFilterKey === 'all' ? 'open' : activeFilterKey

    const activeFilter = this.getItem(key)
    if (!activeFilter) return false

    this._entities.inboxesStore.localInboxes.setCurrentInbox(activeFilterKey)
    this._filter = activeFilter

    return true
  }

  isQuickFilter = (key: IResponseConversationsQuickFilterKey) => {
    return this.quickFilters.includes(key)
  }

  get filters() {
    let filters = Array.from(this._filtersMap.values())

    if (this._config.showFilterByKeys?.length) {
      filters = filters.filter((item) => this._config.showFilterByKeys?.includes(item.key))
    }

    if (this._config.disableQuickFilters || this._entities.inboxesStore.isUnifiedInbox) {
      filters = filters.filter(
        (item) => !this.isQuickFilter(item.key as IResponseConversationsQuickFilterKey)
      )
    }

    const isRCS =
      this._entities.inboxesStore.currentInbox instanceof Inbox &&
      this._entities.inboxesStore.currentInbox?.isRCS
    if (isRCS) {
      filters = filters.filter((item) => item.key !== 'nocalls')
    }

    return filters
  }

  get storageKey() {
    if (!this._entities.usersStore.user) return ''

    return `${this._entities.usersStore.user.id}_conversation_filter`
  }

  get isOpen() {
    return this._filter.key === 'open'
  }

  get isPending() {
    return this._filter.key === 'pending'
  }

  get isClosed() {
    return this._filter.key === 'closed'
  }
  get isUnread() {
    return this._filter.key === 'unread'
  }
  get isOutbound() {
    return this._filter.key === 'outbound'
  }
}
